/**
 * Hooks React Query pour les préférences utilisateur
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { preferencesClient } from '../api';
import type { UserPreference, SetPreferenceDto } from '@sphere/user/domain';

const QUERY_KEY = ['user', 'preferences'];

/**
 * Hook pour récupérer toutes les préférences
 */
export function usePreferences() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => preferencesClient.getAll(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook pour récupérer une préférence par clé
 */
export function usePreference(key: string) {
  return useQuery({
    queryKey: [...QUERY_KEY, key],
    queryFn: () => preferencesClient.getByKey(key),
    enabled: !!key,
  });
}

/**
 * Hook pour définir une préférence
 */
export function useSetPreference() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: SetPreferenceDto) => preferencesClient.set(dto),
    onSuccess: (data) => {
      // Mettre à jour le cache
      queryClient.setQueryData<UserPreference[]>(QUERY_KEY, (old) => {
        if (!old) return [data];
        const index = old.findIndex((p) => p.key === data.key);
        if (index >= 0) {
          const updated = [...old];
          updated[index] = data;
          return updated;
        }
        return [...old, data];
      });
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEY, data.key] });
    },
  });
}

/**
 * Hook pour définir plusieurs préférences
 */
export function useSetPreferencesBulk() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (preferences: SetPreferenceDto[]) =>
      preferencesClient.setBulk(preferences),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

/**
 * Hook pour supprimer une préférence
 */
export function useDeletePreference() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (key: string) => preferencesClient.delete(key),
    onSuccess: (_, key) => {
      queryClient.setQueryData<UserPreference[]>(QUERY_KEY, (old) => {
        if (!old) return [];
        return old.filter((p) => p.key !== key);
      });
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEY, key] });
    },
  });
}

/**
 * Hook pour supprimer toutes les préférences
 */
export function useDeleteAllPreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => preferencesClient.deleteAll(),
    onSuccess: () => {
      queryClient.setQueryData(QUERY_KEY, []);
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

/**
 * Helper pour obtenir la valeur d'une préférence depuis le cache
 */
export function usePreferenceValue<T = unknown>(
  key: string,
  defaultValue?: T
): T | undefined {
  const { data } = usePreferences();
  const pref = data?.find((p) => p.key === key);
  return (pref?.value as T) ?? defaultValue;
}
